import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './clientCreatePkb.module.css';

import HookForm from '../../../../components/HookForm/HookForm';
import Button from '../../../../components/Button/Button';
import Hint from '../../../../components/Hint';

import pkbRequestConsent from '../../../../api/clients/pkbRequestConsent';
import pkbPollConsent from '../../../../api/clients/pkbPollConsent';
import phoneBelongsIinValidate from '../../../../api/bmg/phoneBelongsIinValidate';
import { BMG_PHONE_MISMATCH_MESSAGE, BMG_UNAVAILABLE_MESSAGE } from '../../../../utils/clientContact';
import alert from '../../../../helpers/alert';
import { phone as phoneMask, iin as iinMask } from '../../../../constants/mask';

// ТД ПКБ v7: КДП принимает не чаще одного запроса в 10 секунд на ИИН,
// иначе GBDFL_KDP_ERROR «KDP: too many requests».
const POLL_INTERVAL_MS = 11000;
// Столько живёт согласие в ГБД — этот же срок напечатан в СМС клиенту.
// Показывать больше нельзя: клиент отвечает «в срок» по нашему таймеру и промахивается.
const CONSENT_WINDOW_SECONDS = 5 * 60;

const STATUS_LABELS = {
  Pending: 'Ожидаем ответ клиента',
  Created: 'Согласие получено',
  Declined: 'Клиент отказался предоставить доступ к данным',
  Timeout: 'Клиент не ответил на SMS за отведённые 5 минут',
  NoPhone: 'Номер клиента не найден в Базе мобильных граждан',
  Error: 'Не удалось получить данные',
};

const IconId = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="4" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="8.5" cy="10.5" r="1.8" stroke="currentColor" strokeWidth="1.4" />
    <path d="M5.5 15.5c.6-1.6 1.9-2.4 3-2.4s2.4.8 3 2.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <path d="M14 9h4M14 12h4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const IconSms = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
    <path
      d="M4 5.5C4 4.7 4.7 4 5.5 4h13c.8 0 1.5.7 1.5 1.5v9c0 .8-.7 1.5-1.5 1.5H9l-3.6 3.2c-.5.4-1.2.1-1.2-.6V15h-.7C4.7 15 4 14.3 4 13.5v-8Z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <circle cx="8.3" cy="9.5" r="1" fill="currentColor" />
    <circle cx="12" cy="9.5" r="1" fill="currentColor" />
    <circle cx="15.7" cy="9.5" r="1" fill="currentColor" />
  </svg>
);

const IconAlert = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
    <path d="M12 7.5v5.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="12" cy="16.3" r="1.1" fill="currentColor" />
  </svg>
);

const IconRefresh = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path
      d="M20 12a8 8 0 1 1-2.6-5.9M20 4v5h-5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const howItWorksHint = (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 220 }}>
    <div style={{ display: 'flex', gap: 8 }}>
      <span style={{ fontWeight: 700, color: '#b8894f' }}>1.</span>
      <span>Укажите ИИН и телефон клиента</span>
    </div>
    <div style={{ display: 'flex', gap: 8 }}>
      <span style={{ fontWeight: 700, color: '#b8894f' }}>2.</span>
      <span>Клиент подтверждает по SMS от «1414» (5 минут)</span>
    </div>
    <div style={{ display: 'flex', gap: 8 }}>
      <span style={{ fontWeight: 700, color: '#b8894f' }}>3.</span>
      <span>Данные клиента получены</span>
    </div>
  </div>
);

const Steps = ({ active }) => (
  <div className={styles.steps}>
    <div className={`${styles.step} ${active === 1 ? styles.active : ''}`}>
      <span className={styles.stepDot} />
      Данные клиента
    </div>
    <div className={styles.stepLine} />
    <div className={`${styles.step} ${active === 2 ? styles.active : ''}`}>
      <span className={styles.stepDot} />
      SMS-согласие
    </div>
  </div>
);

// onCreated/onManual позволяют встроить этот экран в модалку (создание клиента
// прямо внутри другого флоу) — без них работает как самостоятельная страница.
const ClientCreatePkb = ({ onCreated, onManual }) => {
  const navigate = useNavigate();
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState('form'); // form | waiting | error
  const [consentRequestId, setConsentRequestId] = useState(null);
  const [checkStatus, setCheckStatus] = useState('Pending');
  const [errorMessage, setErrorMessage] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(CONSENT_WINDOW_SECONDS);
  const windowSecondsRef = useRef(CONSENT_WINDOW_SECONDS);
  const [simulated, setSimulated] = useState(false);

  const pollTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);

  const clearTimers = () => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    pollTimerRef.current = null;
    countdownTimerRef.current = null;
  };

  useEffect(() => clearTimers, []);

  const finishWith = (result) => {
    clearTimers();
    if (result.status === 'Created') {
      alert.success('Клиент успешно создан');
      if (onCreated) {
        onCreated(result.clientId);
        return;
      }
      navigate(`/client/${result.clientId}/main-data`);
      return;
    }
    setPhase('error');
    setErrorMessage(result.errorMessage || STATUS_LABELS[result.status] || 'Ошибка');
  };

  const poll = async (id) => {
    try {
      const res = await pkbPollConsent(id);
      const status = res?.data?.status || 'Pending';
      setCheckStatus(status);
      if (status !== 'Pending') {
        finishWith(res.data);
      }
    } catch (e) {
      // сетевая ошибка одного опроса — не прерываем ожидание, попробуем на следующем тике
    }
  };

  const startWaiting = (id, windowSeconds) => {
    windowSecondsRef.current = windowSeconds || CONSENT_WINDOW_SECONDS;

    setConsentRequestId(id);
    setCheckStatus('Pending');
    setSecondsLeft(windowSecondsRef.current);
    setPhase('waiting');

    poll(id);
    pollTimerRef.current = setInterval(() => poll(id), POLL_INTERVAL_MS);
    countdownTimerRef.current = setInterval(() => {
      setSecondsLeft((prev) => Math.max(0, prev - 1));
    }, 1000);
  };

  const submit = async () => {
    const iin = (values.iin || '').replace(/\D/g, '');
    const phone = (values.phoneNumber || '').replace(/\D/g, '');

    if (iin.length !== 12) {
      return alert.error('Проверьте правильность ИИН');
    }
    if (!phone) {
      return alert.error('Укажите номер телефона клиента');
    }

    setLoading(true);

    // Проверка БМГ здесь, а не на шаге 4 сделки: клиент, созданный через eGov,
    // до этого её обходил и узнавал о чужом номере только перед подписанием
    // согласий (сделка 191883). Ручная форма создания проверяет так же.
    try {
      const isPhoneBelongsToClient = await phoneBelongsIinValidate({ phone, iin });
      if (!isPhoneBelongsToClient) {
        setLoading(false);
        return alert.error(BMG_PHONE_MISMATCH_MESSAGE);
      }
    } catch (e) {
      setLoading(false);
      return alert.error(BMG_UNAVAILABLE_MESSAGE);
    }

    try {
      const res = await pkbRequestConsent({
        iin,
        phone,
      });
      setSimulated(!!res.data.simulated);
      startWaiting(res.data.consentRequestId, res.data.expiresInSeconds);
    } catch (e) {
      alert.error(e?.response?.data?.errorMessage || 'Не удалось отправить запрос согласия');
    } finally {
      setLoading(false);
    }
  };

  const formInputs = [
    {
      name: 'iin',
      label: 'ИИН клиента',
      placeholder: '_ _ _ _ _ _ _ _ _ _ _ _',
      mask: iinMask,
      rules: { required: true },
    },
    {
      name: 'phoneNumber',
      label: 'Номер телефона клиента',
      placeholder: '+7(___) ___-__-__',
      mask: phoneMask,
      rules: { required: true },
    },
  ];

  const goManual = () => (onManual ? onManual() : navigate('/client/create'));

  const progressPercent = Math.round(
    ((windowSecondsRef.current - secondsLeft) / windowSecondsRef.current) * 100
  );
  const minutes = Math.floor(secondsLeft / 60);
  const seconds = String(secondsLeft % 60).padStart(2, '0');

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <IconId />
        </div>
        <div>
          <p className={styles.headerTitle}>
            Получение данных клиента с eGov
            <Hint text={howItWorksHint} />
          </p>
          {/* <p className={styles.headerSubtitle}>Создадим карточку клиента после его согласия по SMS</p> */}
        </div>
      </div>

      <Steps active={phase === 'form' ? 1 : 2} />

      {phase === 'form' && (
        <div className={styles.card}>
          {loading && (
            <div className={styles.formOverlay}>
              <span className={styles.formSpinner} />
              <p className={styles.formOverlayText}>Отправляем запрос согласия…</p>
            </div>
          )}
          <div className={styles.forms}>
            <HookForm
              inputs={formInputs}
              grid={{ display: 'grid', rowGap: '18px', margin: 0 }}
              style={{ margin: 0, padding: 0, backgroundColor: 'transparent' }}
              onValuesChange={setValues}
            />
            {/* <p className={styles.fieldHint}>Телефон сохранится как контактный номер клиента в CRM</p> */}
            <div className={styles.actions}>
              <Button text="Отправить запрос согласия" fill full onClick={submit} disabled={loading} />
              <span className={styles.manualLink} onClick={goManual}>
                Заполнить вручную
              </span>
            </div>
          </div>
        </div>
      )}

      {phase === 'waiting' && (
        <div className={styles.waitingCard}>
          <div className={styles.spinnerWrap}>
            <span className={styles.spinnerRing} />
            <span className={styles.spinnerIcon}>
              <IconSms />
            </span>
          </div>
          <p className={styles.waitingTitle}>{STATUS_LABELS[checkStatus] || 'Ожидаем ответ клиента'}</p>
          <p className={styles.waitingSubtitle}>
            {simulated
              ? 'Тестовый контур: согласие подтвердится само, отвечать на SMS не нужно'
              : 'Клиент отвечает 511 на SMS от «1414» — у него 5 минут'}
          </p>

          <div className={styles.countdown}>
            {minutes}:{seconds}
          </div>
          <p className={styles.countdownLabel}>осталось времени</p>

          <div className={styles.progressTrack}>
            <div className={styles.progressFill} style={{ width: `${progressPercent}%` }} />
          </div>

          <button className={styles.refreshButton} onClick={() => poll(consentRequestId)}>
            <IconRefresh />
            Обновить статус
          </button>
        </div>
      )}

      {phase === 'error' && (
        <div className={styles.errorCard}>
          <div className={styles.errorIcon}>
            <IconAlert />
          </div>
          <p className={styles.errorTitle}>Не получилось создать клиента</p>
          <p className={styles.errorMessage}>{errorMessage}</p>
          <div className={styles.errorActions}>
            <Button text="Попробовать ещё раз" fill full onClick={() => setPhase('form')} />
            <span className={styles.manualLink} onClick={goManual}>
              Заполнить вручную
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientCreatePkb;
